import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/shared";
import { cn } from "@/lib/utils";
import { RulesCard, ActivityCard } from "@/components/candidate_portal/AutoApplyParts";

export default function AutoApply() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/autoapply"), api.get("/profile/me")]).then(([a, p]) => { setCfg(a.data); setProfile(p.data); }).catch(() => toast.error("Could not load auto-apply"));
  }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const { data } = await api.put("/autoapply", { enabled: next.enabled, min_fit: next.min_fit, max_total: next.max_total, work_modes: next.work_modes || [] });
      setCfg(data);
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/autoapply/run");
      setCfg(data);
      const n = (data.log || []).filter((e) => e.action === "applied" && Date.now() - Date.parse(e.timestamp) < 60000).length;
      toast.success(n ? `Agent applied to ${n} role${n === 1 ? "" : "s"}` : "Agent ran — no new roles met your bar");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run agent"); }
    finally { setRunning(false); }
  };

  if (!cfg) return <LoadingState label="Loading your agent…" />;
  const log = cfg.log || [];

  return (
    <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6" data-testid="autoapply-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground flex items-center gap-1">
            <button onClick={() => nav("/candidate/jobs")} className="hover:text-foreground" data-testid="autoapply-breadcrumb-jobs">Jobs</button>
            <ChevronRight className="h-3.5 w-3.5" /><span aria-current="page">Auto-apply</span>
          </nav>
          <h1 className="mt-1 text-3xl sm:text-4xl font-display font-semibold tracking-tight">Auto-apply</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">Opt-in. The agent applies to roles that clear your bar, within your cap — every action is logged.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={cn("inline-flex items-center gap-2 text-sm", cfg.enabled ? "text-primary" : "text-muted-foreground")} data-testid="autoapply-status">
            <span className={cn("h-2 w-2 rounded-full", cfg.enabled ? "bg-primary animate-pulse" : "bg-muted-foreground/50")} />{cfg.enabled ? "Active" : "Paused"}
          </span>
          <Button variant={cfg.enabled ? "outline" : "default"} onClick={() => save({ enabled: !cfg.enabled })} disabled={saving} data-testid="autoapply-toggle">{cfg.enabled ? "Pause" : "Turn on"}</Button>
        </div>
      </header>
      {profile?.status !== "confirmed" && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-center justify-between gap-4" data-testid="autoapply-profile-warning">
          <span className="text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-500" /> Confirm your profile so the agent can match roles.</span>
          <Button size="sm" onClick={() => nav("/candidate/profile")} data-testid="autoapply-profile">Build profile</Button>
        </div>
      )}
      <div className="grid lg:grid-cols-[420px_minmax(0,1fr)] gap-5 items-start">
        <RulesCard cfg={cfg} save={save} run={run} running={running} />
        <ActivityCard log={log} cap={cfg.max_total} used={log.filter((e) => e.action === "applied").length} />
      </div>
    </div>
  );
}
